/**
 * Snapshot revision guard: tracks embedded DB revision in localStorage.
 */
export function resolveEmbeddedSnapshotRevision(
  env: Readonly<Record<string, string | undefined>>,
  fallbackRevision = '2026-esan-paquetes-v2',
): string {
  return (env.LOCAL_SQLITE_SNAPSHOT_REVISION ?? '').trim() || fallbackRevision;
}

export function resolveSnapshotRevisionLsKey(
  env: Readonly<Record<string, string | undefined>>,
  fallbackKey = 'esan_paquetes_snapshot_revision',
): string {
  return (env.LOCAL_SQLITE_SNAPSHOT_LSV_KEY ?? '').trim() || fallbackKey;
}

const SNAPSHOT_REVISION = resolveEmbeddedSnapshotRevision(import.meta.env);
const LS_REVISION_KEY = resolveSnapshotRevisionLsKey(import.meta.env);
const LS_UPGRADE_PENDING_KEY = `${LS_REVISION_KEY}:upgrade_pending`;

export function getEmbeddedSnapshotRevision(): string {
  return SNAPSHOT_REVISION;
}

export function readStoredSnapshotRevision(): string | null {
  try {
    if (globalThis.localStorage === undefined) return null;
    return globalThis.localStorage.getItem(LS_REVISION_KEY);
  } catch {
    return null;
  }
}

export function writeStoredSnapshotRevision(): void {
  try {
    if (globalThis.localStorage === undefined) return;
    globalThis.localStorage.setItem(LS_REVISION_KEY, SNAPSHOT_REVISION);
  } catch {
    /* ignore persistence failures */
  }
}

export function didSnapshotRevisionChange(previous: string | null): boolean {
  return previous !== null && previous !== SNAPSHOT_REVISION;
}

export function markSnapshotRevisionUpgradePending(): void {
  try {
    if (globalThis.localStorage === undefined) return;
    globalThis.localStorage.setItem(LS_UPGRADE_PENDING_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function isSnapshotRevisionUpgradePending(): boolean {
  try {
    if (globalThis.localStorage === undefined) return false;
    return globalThis.localStorage.getItem(LS_UPGRADE_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearSnapshotRevisionUpgradePending(): void {
  try {
    if (globalThis.localStorage === undefined) return;
    globalThis.localStorage.removeItem(LS_UPGRADE_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

/** Hard refresh: wipe stored revision + upgrade flag. */
export function clearSnapshotRevisionLocalStorageKeys(): void {
  try {
    if (globalThis.localStorage === undefined) return;
    globalThis.localStorage.removeItem(LS_REVISION_KEY);
    globalThis.localStorage.removeItem(LS_UPGRADE_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

export function noteSnapshotRevisionUpgradeIfNeeded(previousRevision: string | null): void {
  if (didSnapshotRevisionChange(previousRevision)) {
    markSnapshotRevisionUpgradePending();
  }
}
